import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Grid } from '@mui/material'

import { SyncStatus, SyncQueueItem, SyncProgress } from '@/core/offline/types/database.types'
import { syncService } from '@/core/offline'
import { StatusOverviewCards } from './StatusOverviewCards'
import { ProgressBar } from './ProgressBar'
import { DetailedSyncProgress } from './DetailedSyncProgress'
import { ActionButtons } from './ActionButtons'
import { SyncQueueTable } from './SyncQueueTable'

type SyncDashboardProps = {
  syncStatus: SyncStatus
  queueItems: SyncQueueItem[]
  onManualSync: () => void
  onRetryFailed: () => void
  onClearCompleted: () => void
  onRefresh: () => void
  onClearOfflineData: () => void
  loading: boolean
}

export function SyncDashboard({
  syncStatus,
  queueItems,
  onManualSync,
  onRetryFailed,
  onClearCompleted,
  onRefresh,
  onClearOfflineData,
  loading,
}: SyncDashboardProps) {
  const { t } = useTranslation()
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null)

  useEffect(() => {
    // Listen for sync progress updates
    const handleProgress = (progress: SyncProgress) => {
      setSyncProgress(progress)
    }

    syncService.onSyncProgress(handleProgress)

    // Clear progress when sync is not active
    if (!syncStatus.isActive) {
      setSyncProgress(null)
    }

    return () => {
      // Note: In a real implementation, you'd want to remove the listener
      // For now, we'll keep it simple
    }
  }, [syncStatus.isActive])

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <StatusOverviewCards syncStatus={syncStatus} />
      </Grid>

      {syncStatus.isActive && (
        <Grid item xs={12}>
          <ProgressBar syncStatus={syncStatus} />
        </Grid>
      )}

      {syncProgress && syncStatus.isActive && (
        <Grid item xs={12}>
          <DetailedSyncProgress syncProgress={syncProgress} />
        </Grid>
      )}

      <Grid item xs={12}>
        <ActionButtons
          onManualSync={onManualSync}
          onRetryFailed={onRetryFailed}
          onClearCompleted={onClearCompleted}
          onRefresh={onRefresh}
          onClearOfflineData={onClearOfflineData}
          loading={loading}
          syncStatus={syncStatus}
        />
      </Grid>

      <Grid item xs={12}>
        <SyncQueueTable queueItems={queueItems} />
      </Grid>
    </Grid>
  )
}
